import { useEffect, useState } from "react";
import {
  getStats,
  getUsageByAgent,
  getUsageByModel,
  getUsageCostSummary,
  getUsageDaily,
} from "../../api/admin";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import {
  ChartBar,
  Cpu,
  CurrencyCircleDollar,
  Robot,
  TrendUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { isSuperAdminRole } from "../../utils/roles";

/**
 * AdminUsageCostPage - LLM usage metrics, cost estimates, and breakdowns
 */
export default function AdminUsageCostPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [costData, setCostData] = useState(null);
  const [modelData, setModelData] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUsageData = async () => {
      setLoading(true);
      setError(null);

      const [statsResult, costResult, modelResult, agentResult, dailyResult] =
        await Promise.allSettled([
          getStats(),
          getUsageCostSummary({ days: 30 }),
          getUsageByModel({ days: 30 }),
          getUsageByAgent({ days: 30 }),
          getUsageDaily({ days: 30 }),
        ]);

      if (statsResult.status === "rejected") {
        console.error("Failed to load usage stats:", statsResult.reason);
        setError(
          statsResult.reason?.response?.data?.detail ||
            "Failed to load usage and cost data"
        );
        toast.error("Failed to load usage and cost data");
      } else {
        setStats(statsResult.value);
      }

      if (costResult.status === "fulfilled") setCostData(costResult.value);
      if (modelResult.status === "fulfilled") setModelData(modelResult.value);
      if (agentResult.status === "fulfilled") setAgentData(agentResult.value);
      if (dailyResult.status === "fulfilled") setDailyData(dailyResult.value);

      setLoading(false);
    };

    loadUsageData();
  }, []);

  if (loading) {
    return (
      <div data-testid="admin-usage-cost-page">
        <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-6">
          Usage and Cost
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {[...Array(2)].map((_, index) => (
            <Card key={index} className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3">
                <Skeleton className="h-5 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Skeleton className="h-44 w-full rounded-sm" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div data-testid="admin-usage-cost-page">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-sm">
          {error || "Failed to load usage and cost data"}
        </div>
      </div>
    );
  }

  const { usage } = stats;
  const isSuperAdmin = isSuperAdminRole(user?.role);

  return (
    <div data-testid="admin-usage-cost-page">
      <h2 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-1">
        Usage and Cost
      </h2>
      <p className="text-sm text-slate-500 mb-6" data-testid="usage-cost-scope-label">
        {isSuperAdmin
          ? "System-wide usage across all organizations"
          : `Showing usage for ${user?.organization_name || "your organization"} only`}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatCard
          title="Total Tokens"
          value={usage.total_tokens?.toLocaleString() || "0"}
          subtitle={
            usage.total_tokens === 0
              ? "No usage yet"
              : `Input: ${usage.total_input?.toLocaleString() || 0} / Output: ${usage.total_output?.toLocaleString() || 0}`
          }
          icon={<CurrencyCircleDollar size={24} className="text-amber-600" />}
          testId="stat-total-tokens"
        />
        <StatCard
          title="Total LLM Calls"
          value={usage.total_calls?.toLocaleString() || "0"}
          subtitle={usage.total_calls === 0 ? "No calls yet" : null}
          icon={<ChartBar size={24} className="text-purple-600" />}
          testId="stat-total-calls"
        />
      </div>

      {costData && (
        <Card className="border-slate-200 rounded-sm mb-6" data-testid="usage-cost-card">
          <CardHeader>
            <CardTitle className="font-cabinet text-lg font-bold flex items-center gap-2">
              <TrendUp size={20} />
              Usage &amp; Cost (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500">Estimated Cost</p>
                <p className="text-xl font-cabinet font-black text-slate-900">
                  ${costData.total_cost?.toFixed(4) || "0.00"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Calls</p>
                <p className="text-xl font-cabinet font-black text-slate-900">
                  {costData.total_calls?.toLocaleString() || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Images Processed</p>
                <p className="text-xl font-cabinet font-black text-slate-900">
                  {costData.total_images?.toLocaleString() || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Cached Tokens</p>
                <p className="text-xl font-cabinet font-black text-slate-900">
                  {costData.total_cached_tokens?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-400 italic">
              Cost is indicative only — based on configurable model pricing estimates.
            </p>
          </CardContent>
        </Card>
      )}

      {modelData?.by_model?.length > 0 && (
        <Card className="border-slate-200 rounded-sm mb-6" data-testid="usage-by-model-card">
          <CardHeader>
            <CardTitle className="font-cabinet text-lg font-bold flex items-center gap-2">
              <Cpu size={20} />
              Top Models
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {modelData.by_model.slice(0, 5).map((model) => (
                <div
                  key={model.model}
                  className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0"
                >
                  <span
                    className="text-sm text-slate-700 font-mono truncate max-w-[200px]"
                    title={model.model}
                  >
                    {model.model}
                  </span>
                  <div className="flex gap-4 text-xs text-slate-500">
                    <span>{model.calls} calls</span>
                    <span>${model.estimated_cost?.toFixed(4)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {agentData?.by_agent?.length > 0 && (
        <Card className="border-slate-200 rounded-sm mb-6" data-testid="usage-by-agent-card">
          <CardHeader>
            <CardTitle className="font-cabinet text-lg font-bold flex items-center gap-2">
              <Robot size={20} />
              Top Agents / Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {agentData.by_agent.slice(0, 5).map((agent) => (
                <div
                  key={agent.agent_id}
                  className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0"
                >
                  <div>
                    <span className="text-sm text-slate-700">{agent.agent_id}</span>
                    {agent.feature && (
                      <span className="text-xs text-slate-400 ml-2">({agent.feature})</span>
                    )}
                  </div>
                  <div className="flex gap-4 text-xs text-slate-500">
                    <span>{agent.calls} calls</span>
                    <span>${agent.estimated_cost?.toFixed(4)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {dailyData?.daily?.length > 0 && (
        <Card className="border-slate-200 rounded-sm mb-6" data-testid="usage-daily-card">
          <CardHeader>
            <CardTitle className="font-cabinet text-lg font-bold flex items-center gap-2">
              <ChartBar size={20} />
              Daily Usage (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-slate-600">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-1 pr-4">Date</th>
                    <th className="text-right py-1 pr-4">Calls</th>
                    <th className="text-right py-1 pr-4">Input</th>
                    <th className="text-right py-1 pr-4">Output</th>
                    <th className="text-right py-1 pr-4">Images</th>
                    <th className="text-right py-1">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyData.daily.slice(-14).map((day) => (
                    <tr key={day.date} className="border-b border-slate-50">
                      <td className="py-1 pr-4">{day.date}</td>
                      <td className="text-right py-1 pr-4">{day.calls}</td>
                      <td className="text-right py-1 pr-4">
                        {day.input_tokens?.toLocaleString()}
                      </td>
                      <td className="text-right py-1 pr-4">
                        {day.output_tokens?.toLocaleString()}
                      </td>
                      <td className="text-right py-1 pr-4">{day.image_count}</td>
                      <td className="text-right py-1">
                        ${day.estimated_cost?.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, testId }) {
  return (
    <Card className="border-slate-200 rounded-sm" data-testid={testId}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-600">
            {title}
          </CardTitle>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-cabinet font-black text-slate-900">
          {value}
        </div>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
